import Phaser from "phaser";
import type { MonsterConfig } from "@/phaser/core/monsters/monsterCatalog";
import type { ObstacleConfig } from "@/phaser/core/obstacles/obstaclesCatalog";
import type { QueueItem } from "@phaser/core/queueItem/queueItem";

// 큐 아이템을 제공하는 인터페이스 정의
interface QueueItemProviderConfig {
  monsters: MonsterConfig[];
  obstacles: ObstacleConfig[];
  obstacleInterval?: number;
  maxConsecutiveObstacles?: number; // 연속 등장 제한
}

// 게임 상태(점수 등)에 따라 큐 아이템 제공 방식을 조절할 수 있는 기본 구현체
export class DefaultQueueItemProvider {
  private monsters: MonsterConfig[];
  private obstacles: ObstacleConfig[];

  private maxConsecutiveObstacles: number;
  private obstacleInterval: number;
  private consecutiveObstacles = 0;
  private currentScore = 0;
  private spawnCount = 0;

  constructor(config: QueueItemProviderConfig) {
    this.monsters = config.monsters;
    this.obstacles = config.obstacles;

    this.obstacleInterval = 30;
    this.maxConsecutiveObstacles = config.maxConsecutiveObstacles ?? 1;
  }

  // 게임 상태에 따라 장애물 등장 확률 조절 현재 점수 구간별로 확률 변경
  updateByGameState(score: number) {
    this.currentScore = score;
  }

  // 현재 점수에 따라 등장 가능한 몬스터 목록 반환 (최소 점수 조건 충족 여부 확인)
  private getAvailableMonsters(): MonsterConfig[] {
    const available = this.monsters.filter(
      (monster) => this.currentScore >= (monster.minSpawnScore ?? 0),
    );

    return available.length > 0 ? available : this.monsters;
  }

  // 큐 아이템 제공 메서드: 몬스터 또는 장애물 반환
  getNextItem(): QueueItem {
    this.spawnCount += 1;

    const canSpawnObstacle =
      this.obstacles.length > 0 &&
      this.consecutiveObstacles < this.maxConsecutiveObstacles;

    const shouldSpawnObstacle =
      canSpawnObstacle && this.spawnCount % this.obstacleInterval === 0;

    if (shouldSpawnObstacle) {
      this.consecutiveObstacles += 1;

      return {
        kind: "OBSTACLE",
        data: Phaser.Utils.Array.GetRandom(this.obstacles),
      };
    }

    this.consecutiveObstacles = 0;

    const availableMonsters = this.getAvailableMonsters();

    return {
      kind: "MONSTER",
      data: Phaser.Utils.Array.GetRandom(availableMonsters),
    };
  }
}
